import _extends from "@babel/runtime/helpers/esm/extends";
import { createElement } from "@wordpress/element";

/**
 * WordPress dependencies
 */
import { useRef, useEffect, useState, Fragment } from '@wordpress/element';
import { __, sprintf } from '@wordpress/i18n';
import { PanelBody, PanelRow, Button, Notice, Icon } from '@wordpress/components';
import { store as blockEditorStore } from '@wordpress/block-editor';
import { useDispatch } from '@wordpress/data';
import { convertSecondsToTimeCode } from '@media-manager/time-utils';
import { PlayPauseButton, MediaConnectIcon } from '@media-manager/components';
/**
 * Internal dependencies
 */

export function MediaItem({
  elementType: type,
  source,
  id,
  showTypeLabel,
  onPlayToggle = () => {},
  onItemSelect = () => {},
  onReady = () => {},
  onRemove = () => {}
}) {
  const itemReference = useRef();
  const [isPlaying, setIsPlaying] = useState(false);

  function onPlayHandler() {
    setIsPlaying(true);
  }

  function onPauseHandler() {
    setIsPlaying(false);
  }

  useEffect(() => {
    if (!(itemReference !== null && itemReference !== void 0 && itemReference.current)) {
      return;
    } // Construct media element.


    const mediaElement = document.createElement(type);
    mediaElement.src = source; // Insert player into the DOM.

    itemReference.current.appendChild(mediaElement);
    onReady(mediaElement, id);
    mediaElement.addEventListener('play', onPlayHandler);
    mediaElement.addEventListener('pause', onPauseHandler);
    return function () {
      onRemove(mediaElement, id);
      mediaElement.removeEventListener('play', onPlayHandler);
      mediaElement.removeEventListener('pause', onPauseHandler);
    };
  }, [itemReference]);
  return createElement("div", {
    className: "media__item-wrapper",
    onClick: () => onItemSelect(id)
  }, createElement("div", {
    className: "media__item",
    ref: itemReference
  }), createElement("div", {
    className: "media__item-metadata"
  }, createElement(Icon, {
    className: "media__item-icon",
    icon: `format-${type}`,
    size: 36
  }), showTypeLabel && createElement("strong", null, " ", type), createElement(PlayPauseButton, {
    className: "media__item-play-button",
    isPaused: !isPlaying,
    onClick: event => {
      var _itemReference$curren;

      event.stopPropagation();
      onPlayToggle((_itemReference$curren = itemReference.current) === null || _itemReference$curren === void 0 ? void 0 : _itemReference$curren.querySelector(type), id);
    },
    isSmall: true
  })));
}
export function MediaItemPanelBody({
  source,
  isReady,
  isNotAvailable,
  isNotDefined,
  onReplace,
  onUnlink,
  isMediaInherited
}) {
  const {
    selectBlock
  } = useDispatch(blockEditorStore);
  return createElement(PanelBody, {
    className: "media-source-panel",
    title: __('Media Source Consumer', 'media-manager')
  }, createElement("p", null, __('This block is connected to a media source.', 'media-manager')), (!isReady || isNotAvailable || isNotDefined) && createElement(Fragment, null, createElement(PanelRow, null, createElement(Notice, {
    spokenMessage: null,
    status: isNotAvailable && !isNotDefined ? 'error' : 'warning',
    isDismissible: false
  }, isNotAvailable && !isNotDefined ? __('Media is not available anymore', 'media-manager') : __('No media linked to this block', 'media-manager'))), createElement(PanelRow, null, createElement(Button, {
    isSecondary: true,
    isSmall: true,
    label: __('Link to a media source', 'media-manager'),
    onClick: onReplace
  }, __('Link to Media', 'media-manager')))), source && createElement(Fragment, null, createElement(PanelRow, null, createElement(MediaItem, _extends({}, source, {
    showTypeLabel: true,
    onPlayToggle: element => {
      if (!element) {
        return;
      }

      if (element.paused) {
        return element.play();
      }

      element.pause();
    }
  }))), createElement(PanelRow, null, createElement("ul", null, createElement("li", null, __('Filename:', 'media-manager'), createElement("strong", null, " ", source.source)), createElement("li", null, __('Duration:', 'media-manager'), createElement("strong", null, ' ', convertSecondsToTimeCode(source.duration))), createElement("li", null, sprintf(
  /* translators: %1$s: block title, %2$s: author name. */
  __('Connected to a %1$s.', 'media-manager'), source.elementType), createElement(Button, {
    className: "components-button__focus",
    isLink: true,
    onClick: () => {
      if (!source.mediaBlockClientId) {
        return;
      }

      selectBlock(source.mediaBlockClientId);
    }
  }, __('Focus', 'media-manager'))))), !isMediaInherited && createElement(PanelRow, null, createElement("div", {
    className: "media-source-panel__actions"
  }, createElement(Button, {
    isSecondary: true,
    isSmall: true,
    label: __('Replacing linked media source', 'media-manager'),
    onClick: onReplace
  }, __('Replace Media', 'media-manager')), createElement(Button, {
    isTertiary: true,
    isSmall: true,
    label: __('Unlink media source', 'media-manager'),
    onClick: () => onUnlink(true)
  }, __('Unlink Media', 'media-manager'))))));
}
/**
 * Block settings Pane to show info
 * about block as media provider
 */

export function MediaConnectProviderPanelBody() {
  return createElement(PanelBody, {
    icon: MediaConnectIcon,
    className: "media-source-panel",
    title: __('Media Source Provider', 'media-manager')
  }, createElement("p", null, __('This block has been defined as media provider, meaning you can connect and command it from other blocks.', 'media-manager')));
}
export function MediaSelector({
  media,
  onMediaSelect
}) {
  const [mediaPlayingElementRef, setMediaPlayingElementRef] = useState();

  if (!(media !== null && media !== void 0 && media.length)) {
    return null;
  }

  return createElement("div", {
    className: "media-selector"
  }, createElement("h4", null, __('Pick up a media from the media sources list available for this entrance.', 'media-manager')), createElement("ul", null, media.map(function ({
    id,
    ...other
  }) {
    return createElement("li", {
      key: `item-${id}`
    }, createElement(MediaItem, _extends({
      id: id
    }, other, {
      isPaused: mediaPlayingElementRef === null || mediaPlayingElementRef === void 0 ? void 0 : mediaPlayingElementRef.paused,
      onPlayToggle: element => {
        // Seek previous media element by media Id.
        // If so, stop it.
        if (mediaPlayingElementRef && mediaPlayingElementRef !== element) {
          mediaPlayingElementRef === null || mediaPlayingElementRef === void 0 ? void 0 : mediaPlayingElementRef.pause();
        }

        if (element.paused) {
          setMediaPlayingElementRef(element);
          return element.play();
        }

        setMediaPlayingElementRef(null);
        element.pause();
      },
      onItemSelect: onMediaSelect
    })));
  })));
}
//# sourceMappingURL=index.js.map